import React, { useState } from "react";
import { ArrowUpRight, ShoppingBag, X } from "lucide-react";
import { api } from "../lib/api";

export const PLATFORMS = {
    "Mercado Livre": { bg: "#FFE600", text: "#2D3277", border: "#E5D570", label: "Mercado Livre" },
    "Shopee": { bg: "#EE4D2D", text: "#FFFFFF", border: "#D63D1B", label: "Shopee" },
    "Amazon": { bg: "#FF9900", text: "#0F1111", border: "#E68A00", label: "Amazon" },
    "AliExpress": { bg: "#E62E04", text: "#FFFFFF", border: "#B82503", label: "AliExpress" },
    "Magalu": { bg: "#0086FF", text: "#FFFFFF", border: "#0070DB", label: "Magazine Luiza" },
    "Site próprio": { bg: "#C5A059", text: "#FFFFFF", border: "#B38F4D", label: "Site oficial" },
    "Outra": { bg: "#1A1A1A", text: "#FFFFFF", border: "#1A1A1A", label: "Outra plataforma" },
};

export const PLATFORM_OPTIONS = Object.keys(PLATFORMS);

// Retorna array de links ativos (compatível com produtos antigos com mercado_livre_url)
export const getActiveLinks = (product) => {
    const links = (product?.external_links || []).filter((l) => l.is_active && l.url);
    if (links.length === 0 && product?.mercado_livre_url) {
        return [{ platform: "Mercado Livre", url: product.mercado_livre_url, is_active: true }];
    }
    return links;
};

const fireTrack = (product, platform) => {
    api.post("/track", {
        type: "purchase_click",
        product_id: product.id,
        category_slug: product.category_slug,
        platform,
    }).catch(() => {});
};

const PlatformPicker = ({ product, links, onClose }) => {
    if (!links.length) return null;
    return (
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/60 p-0 sm:p-4" onClick={onClose} data-testid="platform-picker">
            <div onClick={(e) => e.stopPropagation()} className="bg-white w-full sm:max-w-md rounded-t-lg sm:rounded-md shadow-2xl">
                <div className="px-6 pt-6 pb-4 border-b border-brand-border relative">
                    <button onClick={onClose} className="absolute top-4 right-4 w-9 h-9 hover:bg-brand-bg rounded-sm flex items-center justify-center" data-testid="platform-picker-close"><X className="w-4 h-4" /></button>
                    <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-brand-gold mb-1.5">Onde você quer comprar?</div>
                    <h3 className="font-heading font-bold text-xl text-brand-ink leading-tight">Escolha a plataforma</h3>
                    <p className="text-sm text-brand-inkSoft mt-2">Você pode escolher em qual destas plataformas comprar. <strong>Em todas elas está o mesmo produto.</strong></p>
                </div>
                <div className="p-6 space-y-2.5">
                    {links.map((l, i) => {
                        const meta = PLATFORMS[l.platform] || PLATFORMS["Outra"];
                        return (
                            <a
                                key={i}
                                href={l.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => { fireTrack(product, l.platform); }}
                                data-testid={`platform-link-${l.platform}`}
                                className="group flex items-center justify-between gap-3 px-5 py-4 rounded-md border-2 transition-transform hover:-translate-y-0.5"
                                style={{ background: meta.bg, color: meta.text, borderColor: meta.border }}
                            >
                                <div className="flex items-center gap-3">
                                    <ShoppingBag className="w-5 h-5" strokeWidth={2.2} />
                                    <div className="text-left">
                                        <div className="font-heading font-bold text-base leading-tight">{l.platform}</div>
                                        <div className="text-[11px] opacity-75">Comprar em {meta.label}</div>
                                    </div>
                                </div>
                                <ArrowUpRight className="w-5 h-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2.5} />
                            </a>
                        );
                    })}
                </div>
                <div className="px-6 pb-6 text-[11px] text-brand-inkSoft text-center">
                    Você será redirecionado para a plataforma escolhida para finalizar a compra.
                </div>
            </div>
        </div>
    );
};

// Botão grande de compra (página detalhe do produto)
export const BuyButtonLarge = ({ product, className = "" }) => {
    const links = getActiveLinks(product);
    const [open, setOpen] = useState(false);

    if (links.length === 0) {
        return (
            <div data-testid="buy-button-none" className={`bg-brand-bg border border-brand-border text-brand-inkSoft px-8 py-5 text-center text-sm rounded-md ${className}`}>
                Em breve disponível. Cadastre-se em "Receba novidades" para ser avisado.
            </div>
        );
    }

    if (links.length === 1) {
        const l = links[0];
        const meta = PLATFORMS[l.platform] || PLATFORMS["Outra"];
        return (
            <a
                href={l.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => fireTrack(product, l.platform)}
                data-testid="buy-on-ml-button"
                className={`group inline-flex items-center justify-center gap-3 px-8 py-5 font-heading font-bold text-base sm:text-lg rounded-md shadow-sm border-2 transition-transform hover:-translate-y-0.5 ${className}`}
                style={{ background: meta.bg, color: meta.text, borderColor: meta.border }}
            >
                <ShoppingBag className="w-5 h-5" strokeWidth={2.2} />
                Comprar agora em {l.platform}
                <ArrowUpRight className="w-5 h-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2.5} />
            </a>
        );
    }

    return (
        <>
            <button
                onClick={() => setOpen(true)}
                data-testid="buy-on-ml-button"
                className={`group inline-flex items-center justify-center gap-3 px-8 py-5 font-heading font-bold text-base sm:text-lg rounded-md shadow-sm border-2 bg-brand-gold text-white border-brand-goldHover hover:bg-brand-goldHover transition-transform hover:-translate-y-0.5 ${className}`}
            >
                <ShoppingBag className="w-5 h-5" strokeWidth={2.2} />
                Comprar agora · {links.length} plataformas
                <ArrowUpRight className="w-5 h-5" strokeWidth={2.5} />
            </button>
            {open && <PlatformPicker product={product} links={links} onClose={() => setOpen(false)} />}
        </>
    );
};

// Botão compacto (card de produto)
export const BuyButtonCard = ({ product }) => {
    const links = getActiveLinks(product);
    const [open, setOpen] = useState(false);

    if (links.length === 0) {
        return (
            <div className="flex-1 inline-flex items-center justify-center gap-1.5 bg-brand-bg text-brand-inkSoft border border-brand-border px-4 py-2.5 text-sm font-semibold rounded-sm" data-testid={`btn-buy-${product.slug}`}>
                Em breve
            </div>
        );
    }

    if (links.length === 1) {
        const l = links[0];
        return (
            <a
                href={l.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => fireTrack(product, l.platform)}
                data-testid={`btn-buy-${product.slug}`}
                className="flex-1 inline-flex items-center justify-center gap-1.5 bg-brand-gold text-white hover:bg-brand-goldHover transition-colors px-4 py-2.5 text-sm font-semibold rounded-sm"
            >
                Comprar <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={2.5} />
            </a>
        );
    }

    return (
        <>
            <button
                onClick={() => setOpen(true)}
                data-testid={`btn-buy-${product.slug}`}
                className="flex-1 inline-flex items-center justify-center gap-1.5 bg-brand-gold text-white hover:bg-brand-goldHover transition-colors px-4 py-2.5 text-sm font-semibold rounded-sm"
            >
                Comprar · {links.length} <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={2.5} />
            </button>
            {open && <PlatformPicker product={product} links={links} onClose={() => setOpen(false)} />}
        </>
    );
};

export default BuyButtonLarge;
